import { SplitNumberInput } from '@components'

import { Color, Flex, Space, Spacing, Text } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { MAX_INVOICE_DISCOUNT_PERCENT } from '@features/invoices/constants/maxInvoiceDiscountPercent'
import { InvoiceDiscountType } from '@features/invoices/enums/invoiceDiscountType'
import { InvoiceFormRowLine } from '@features/invoices/types/invoiceForm/invoiceFormLine/invoiceFormRowLine'
import { clampInvoiceDiscountValue } from '@features/invoices/utils/clampInvoiceDiscountValue'
import { getInvoiceDiscountTypeSelectItems } from '@features/invoices/utils/getInvoiceDiscountTypeSelectItems'
import { InvoiceDiscountCalculator } from '@features/invoices/utils/invoiceDiscountCalculator'
import { isAmountDiscountType } from '@features/invoices/utils/isAmountDiscountType'
import { isInvoiceFormRowLine } from '@features/invoices/utils/isInvoiceFormRowLine'
import { isPercentageDiscountType } from '@features/invoices/utils/isPercentageDiscountType'

import { useFormContext, useWatch } from '../../../../hooks'
import { useDemoInvoiceLocale } from '../../hooks/useDemoInvoiceLocale'
import { DemoInvoiceFormSchema } from '../../types/demoInvoiceFormSchema'
import * as Styled from './styles'

interface DemoInvoiceLineDiscountProps {
  lineIndex: number
}

export const DemoInvoiceLineDiscount = ({ lineIndex }: DemoInvoiceLineDiscountProps): ReactElement => {
  const { t } = useTranslation()
  const locale = useDemoInvoiceLocale()
  const { control, FormItem, getValues, setValue } = useFormContext<DemoInvoiceFormSchema>()
  const currency = useWatch({ control, name: 'currency' })
  const discountType = useWatch({ control, name: 'discountType' })
  const line = useWatch({ control, name: `invoiceLines.${lineIndex}` }) as InvoiceFormRowLine

  const discountTypeSelectItems = useMemo(() => getInvoiceDiscountTypeSelectItems(currency), [currency])

  const isPercentageDiscount = isPercentageDiscountType(discountType)
  const isAmountDiscount = isAmountDiscountType(discountType)

  const hasDiscountError = useMemo(() => {
    const { discount, quantity, unitPrice, vatRatePercent } = line
    const { validationIssues } = InvoiceDiscountCalculator.calculateLine(
      { discount, quantity, unitPrice, vatRatePercent },
      discountType,
      currency,
    )

    return InvoiceDiscountCalculator.hasDiscountExceedsBaseIssue(validationIssues)
  }, [currency, discountType, line])

  const handleDiscountTypeChange = useCallback(
    (newDiscountType: InvoiceDiscountType) => {
      setValue('discountType', newDiscountType)

      for (const [invoiceLineIndex, invoiceLine] of getValues('invoiceLines').entries()) {
        if (isInvoiceFormRowLine(invoiceLine)) {
          setValue(
            `invoiceLines.${invoiceLineIndex}.discount`,
            clampInvoiceDiscountValue(invoiceLine.discount, newDiscountType),
          )
        }
      }
    },
    [getValues, setValue],
  )

  return (
    <Flex flexDirection="column" gap={Spacing.S4}>
      <FormItem
        name={`invoiceLines.${lineIndex}.discount`}
        render={({ field: { onChange, ...rest } }) => (
          <SplitNumberInput
            key={discountType}
            {...rest}
            alignment="left"
            aria-label={t('external_invoices.editor.form.field.line.header.discount')}
            dropdownOptions={discountTypeSelectItems}
            error={hasDiscountError}
            locale={locale}
            max={isPercentageDiscount ? MAX_INVOICE_DISCOUNT_PERCENT : undefined}
            min={0}
            onChange={(discountValue) => {
              if (discountValue === undefined) {
                return
              }

              onChange(clampInvoiceDiscountValue(discountValue, discountType))
            }}
            onOptionChange={handleDiscountTypeChange}
            selectedOption={discountType}
            size="sm"
            withFormatting={isAmountDiscount}
          />
        )}
      />
      {hasDiscountError && (
        <>
          <Space size="s6" />
          <Styled.DiscountError>
            <Text color={Color.Red700} variant="bodySM">
              {t('external_invoices.editor.form.field.line.field.discount.error')}
            </Text>
          </Styled.DiscountError>
        </>
      )}
    </Flex>
  )
}
